import { useState } from "react";
import { Link } from "react-router-dom";

import { ArrowIcon, GitHubIcon } from "../components/Icons";
import { ProjectVisual } from "../components/ProjectVisual";
import { RevealItem, stagger } from "../components/Reveal";
import { Shell } from "../components/Shell";
import { Empty, Failed, Loading } from "../components/States";
import { TiltPanel } from "../components/TiltPanel";
import { useApi } from "../hooks/useApi";
import { api, type ProjectSummary } from "../lib/api";

/**
 * The project list.
 *
 * One list. An earlier version split a "coursework" tier into a subordinate
 * section, which undersold work that has real datasets, real training runs and
 * written-up results — the tier was a presentation decision, not a fact about
 * the projects. Ordering still carries emphasis; a separate heading was
 * carrying an apology.
 */

function TechTag({ label }: { label: string }) {
  return (
    <li className="rounded-full border border-edge px-2.5 py-0.5 font-mono text-2xs text-text-dim">
      {label}
    </li>
  );
}

/**
 * A project card.
 *
 * Three actions, and only the first is the card's own heading link: "View
 * details" is the primary route in, and the repo and live links are secondary
 * and *only rendered when the data has them*. A greyed-out "Source" button on
 * a project with no public repo tells a visitor nothing except that something
 * is missing; DATA-NOTES flags two flagship projects in exactly that state,
 * and the detail page says so in words instead.
 *
 * External links carry a visually-hidden project name, because "GitHub"
 * repeated six times down a page is six identical links in a screen reader's
 * link list with no way to tell them apart.
 */
function ProjectCard({ project }: { project: ProjectSummary }) {
  return (
    <TiltPanel>
      <div className="flex h-full flex-col">
        {/* The visual is decorative and sits behind a fade, so the title
            never has to compete with it for contrast. */}
        {/* Taller now that these are real screenshots, and the fade only
            covers the bottom third — a full-height wash was fine over a
            generated pattern and swallows an actual image. */}
        {/* A link, because the cursor already promised one.
            `data-cursor="View"` swaps the pointer for a "View" pill over this
            area — and clicking did nothing. A cursor that says View over dead
            space is worse than no custom cursor: it is the site telling you
            something is clickable and then not being. Everyone who tried it
            found the same bug.

            `aria-hidden` with `tabIndex={-1}` is what keeps this from being
            the panel-wide anchor the title link deliberately avoids. It adds
            a click target for a mouse and nothing at all for a keyboard or a
            screen reader, both of which already have the title link and the
            "View details" link to the same place. Three identical stops would
            be worse than one. */}
        <Link
          to={`/projects/${project.slug}`}
          aria-hidden="true"
          tabIndex={-1}
          data-cursor="View"
          className="relative block aspect-[16/9] overflow-hidden rounded-t-xl bg-canvas"
        >
          <ProjectVisual slug={project.slug} coverUrl={project.cover_image_url} />
          <div
            aria-hidden="true"
            className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-b from-transparent to-surface"
          />
        </Link>

        <div className="flex flex-1 flex-col p-7 pt-4">
          <h3 className="text-lg">
            {/* The whole panel is not a link. A panel-wide anchor makes the
                accessible name the entire contents, which a screen reader
                reads out in full before saying "link". */}
            <Link
              to={`/projects/${project.slug}`}
              className="text-text transition-colors duration-300 group-hover:text-accent"
            >
              {project.title}
            </Link>
          </h3>
          <p className="mt-3 text-base text-text-dim">{project.tagline}</p>

          <ul className="mt-6 flex flex-wrap gap-1.5">
            {project.tech_stack.slice(0, 6).map((tech) => (
              <TechTag key={tech} label={tech} />
            ))}
          </ul>

          <p className="mt-6 font-mono text-2xs text-text-dim">
            {[project.role, project.date_display].filter(Boolean).join(" · ")}
          </p>

          <div className="mt-auto flex flex-wrap items-center gap-x-5 gap-y-2 pt-6 text-2xs">
            <Link
              to={`/projects/${project.slug}`}
              className="group/link inline-flex items-center gap-1.5 text-accent transition-colors duration-300"
            >
              View details
              <ArrowIcon className="size-3.5 transition-transform duration-300 group-hover/link:translate-x-0.5" />
            </Link>
            {project.repo_url && (
              <a
                href={project.repo_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-text-dim transition-colors duration-300 hover:text-text"
              >
                <GitHubIcon className="size-3.5" />
                Source
                <span className="sr-only"> for {project.title} on GitHub</span>
              </a>
            )}
            {project.live_url && (
              <a
                href={project.live_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-text-dim transition-colors duration-300 hover:text-text"
              >
                Live
                <span className="sr-only"> site for {project.title}</span>
                <ArrowIcon className="size-3 -rotate-45" />
              </a>
            )}
          </div>
        </div>
      </div>
    </TiltPanel>
  );
}

/**
 * Only tags that narrow something.
 *
 * `/projects/tech` returns every distinct string across ten projects — fifty-odd
 * of them, six rows of buttons above the fold, most matching exactly one
 * project. That is not a filter, it is a list of every value in a column, and a
 * tag that matches one project is a link to that project with extra steps.
 */
const MIN_PROJECTS_PER_TAG = 2;

function usefulTags(projects: readonly ProjectSummary[]): string[] {
  const counts = new Map<string, number>();
  for (const project of projects) {
    for (const tech of project.tech_stack) {
      counts.set(tech, (counts.get(tech) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .filter(([, count]) => count >= MIN_PROJECTS_PER_TAG)
    .sort(
      ([aName, aCount], [bName, bCount]) =>
        bCount - aCount || aName.localeCompare(bName),
    )
    .map(([name]) => name);
}

function filterClass(active: boolean): string {
  return [
    "rounded-full border px-3.5 py-1.5 font-mono text-2xs transition-colors duration-300",
    active
      ? "border-accent-dim bg-accent/[0.08] text-accent"
      : "border-edge text-text-dim hover:border-accent-dim hover:text-text",
  ].join(" ");
}

export function Projects() {
  const [tech, setTech] = useState<string | null>(null);

  // Fetched once, unfiltered, and narrowed in the browser. The API's `tech`
  // parameter exists and is tested, but a dozen projects do not need a round trip
  // per press — filtering here is instant and has no loading state to render.
  const projects = useApi(() => api.projects(), []);

  const everything = projects.data ?? [];
  const tags = usefulTags(everything);
  const all = tech
    ? everything.filter((project) => project.tech_stack.includes(tech))
    : everything;

  return (
    <Shell className="py-24">
      <p className="mb-3 font-mono text-2xs tracking-[0.28em] text-accent uppercase">
        Projects
      </p>
      <h1 className="mb-6 text-xl sm:text-2xl lg:text-3xl">
        What I&rsquo;ve built
      </h1>
      {/* Counted, not written down. "Ten projects" survived three being
          added and read as a stale page — on a site whose argument is that
          its numbers are real, a hand-typed count is the wrong kind of
          detail to get wrong. Blank until the fetch lands, so the sentence
          never renders a number it is about to correct. */}
      <p className="mb-16 max-w-(--spacing-measure) text-lg text-text-dim">
        {everything.length > 0 && `${String(everything.length)} projects — `}
        computer vision, retrieval-augmented generation, production APIs and
        the infrastructure around them. Each one is written up as problem,
        approach, tradeoffs and outcome.
      </p>

      {tags.length > 0 && (
        <div className="mb-16">
          <h2
            className="mb-3 text-2xs tracking-widest text-text-dim uppercase"
            id="filter-label"
          >
            Filter by tech
          </h2>
          <ul className="flex flex-wrap gap-2" aria-labelledby="filter-label">
            <li>
              <button
                type="button"
                onClick={() => {
                  setTech(null);
                }}
                aria-pressed={tech === null}
                className={filterClass(tech === null)}
              >
                All
              </button>
            </li>
            {tags.map((tag) => (
              <li key={tag}>
                <button
                  type="button"
                  onClick={() => {
                    setTech(tag);
                  }}
                  aria-pressed={tech === tag}
                  className={filterClass(tech === tag)}
                >
                  {tag}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* aria-live so a filter change is announced. Without it a keyboard or
          screen-reader user presses a filter and nothing tells them the list
          below changed. */}
      <div aria-live="polite" aria-busy={projects.loading}>
        {projects.loading && <Loading label="projects" />}
        {projects.error && (
          <Failed error={projects.error} what="the projects" />
        )}

        {projects.data && all.length === 0 && (
          <Empty>
            No projects use {tech}.{" "}
            <button
              type="button"
              onClick={() => {
                setTech(null);
              }}
              className="text-text underline decoration-edge underline-offset-4 hover:decoration-accent"
            >
              Clear the filter
            </button>{" "}
            to see all ten.
          </Empty>
        )}

        {all.length > 0 && (
          <ul className="grid gap-6 md:grid-cols-2">
            {all.map((project, index) => (
              // Keyed on the filter as well as the slug: pressing a filter
              // should re-run the entrance on the new set, not leave the
              // survivors sitting still while the rest fade in around them.
              <RevealItem
                key={`${tech ?? "all"}-${project.slug}`}
                delay={stagger(index)}
              >
                <ProjectCard project={project} />
              </RevealItem>
            ))}
          </ul>
        )}

      </div>
    </Shell>
  );
}
